// EnhancedMessage.js - Better message formatting with React Markdown

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

// Simple function to detect and style franchise names
const highlightFranchises = (text) => {
  // List of major franchises to highlight
  const franchises = [
    'Marvel', 'DC', 'Star Wars', 'Star Trek', 'Lord of the Rings', 
    'Harry Potter', 'Game of Thrones', 'Dragon Ball', 'Naruto', 
    'One Piece', 'Attack on Titan', 'My Hero Academia', 'Demon Slayer',
    'X-Men', 'Avengers', 'Justice League', 'Batman', 'Superman', 
    'Spider-Man', 'Iron Man', 'Captain America', 'Thor'
  ];
  
  let processedText = text;
  
  // Apply highlighting for each franchise
  franchises.forEach(franchise => {
    const regex = new RegExp(`\\b${franchise}\\b`, 'gi');
    processedText = processedText.replace(regex, match => `**${match}**`);
  });
  
  return processedText;
};

const EnhancedMessage = ({ role, content, isLoading, theme }) => {
  // Define custom styling based on theme
  const customStyles = {
    '--message-accent-color': theme?.primaryColor || 'var(--accent-color)'
  };
  
  // If it's a loading message, show loading animation
  if (isLoading) {
    return (
      <div className={`message ${role} loading`} style={customStyles}>
        <div className="loading-indicator">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
      </div>
    );
  }
  
  // For user messages, just show the text without markdown formatting
  if (role === 'user') {
    return (
      <div className={`message ${role}`}>
        <div className="message-content">
          {content.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </div>
    );
  }
  
  // For assistant messages, apply formatting with React Markdown
  // First enhance content by highlighting franchise names
  const enhancedContent = highlightFranchises(content);
  
  return (
    <div className={`message ${role}`} style={customStyles}>
      <div className="message-content markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]} // Adds support for tables, strikethrough, etc.
          rehypePlugins={[rehypeHighlight]} // Syntax highlighting for code blocks
          components={{
            // Custom rendering for headings
            // eslint-disable-next-line jsx-a11y/heading-has-content
            h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
            // eslint-disable-next-line jsx-a11y/heading-has-content
            h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
            // eslint-disable-next-line jsx-a11y/heading-has-content
            h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
            
            // Custom rendering for lists
            ul: ({node, ...props}) => <ul className="markdown-ul" {...props} />,
            ol: ({node, ...props}) => <ol className="markdown-ol" {...props} />,
            li: ({node, ...props}) => <li className="markdown-li" {...props} />,
            
            // Custom rendering for paragraphs
            p: ({node, ...props}) => <p className="markdown-p" {...props} />,
            
            // Custom rendering for emphasis
            strong: ({node, ...props}) => <strong className="markdown-strong" {...props} />,
            em: ({node, ...props}) => <em className="markdown-em" {...props} />,
            
            // Custom rendering for links
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            a: ({node, ...props}) => <a className="markdown-link" target="_blank" rel="noopener noreferrer" {...props} />,
            
            // Custom rendering for code blocks
            code: ({node, ...props}) => <code className="markdown-code" {...props} />
          }}
        >
          {enhancedContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default EnhancedMessage; 